import React, { useEffect, useState } from "react";
import { useParams, Link as RouterLink } from "react-router-dom";
import { api } from "../../lib/api";
import { useShellHeader } from "../../Components/AppShell";
import { Card, EmptyState, Skeleton, Pill } from "../../Components/ui";

const TYPE_ICON = {
  CREATED: "🆕", ASSIGNED: "👤", FIRST_ACTIVITY: "✨",
  STATUS_CHANGED: "↔️", STARTED: "▶️", SUBMITTED: "📩",
  APPROVED: "✅", REJECTED: "❌", REOPENED: "🔁",
  COMPLETED: "🏁", OVERRIDE: "⚙️",
};
const TYPE_TONE = {
  CREATED: "neutral", ASSIGNED: "info", FIRST_ACTIVITY: "brand",
  STARTED: "info", SUBMITTED: "violet", APPROVED: "success",
  REJECTED: "danger", REOPENED: "warning", COMPLETED: "success",
  OVERRIDE: "warning",
};

function fmt(d) { return new Date(d).toLocaleString(); }

export default function TaskTimeline() {
  const { taskId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useShellHeader({ title: "Task timeline", breadcrumb: "Analytics" }, []);

  useEffect(() => {
    api(`/analytics/timeline/task/${taskId}`)
      .then((d) => setData(d))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [taskId]);

  if (loading) {
    return (
      <div style={{ maxWidth: 768, margin: "0 auto" }}>
        <Skeleton.Card />
        <div style={{ marginTop: 16 }}><Skeleton.Card /></div>
      </div>
    );
  }
  if (error) return (
    <Card padding="lg">
      <div style={{ fontSize: 14, fontWeight: 600, color: "#B91C1C" }}>Couldn't load timeline</div>
      <div style={{ fontSize: 13, color: "var(--text-tertiary)", marginTop: 4 }}>{error}</div>
    </Card>
  );
  if (!data) return null;

  const { task, events } = data;
  return (
    <div style={{ maxWidth: 768, margin: "0 auto", display: "flex", flexDirection: "column", gap: 16 }}>
      <Card padding="lg">
        <div style={{ fontSize: 11, color: "var(--text-tertiary)", textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 600 }}>Task</div>
        <div style={{ fontSize: 18, fontWeight: 600, color: "var(--text-primary)", marginTop: 4, letterSpacing: "-0.01em" }}>{task.title}</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 16, marginTop: 16 }}>
          <Field label="Status"        value={task.status} />
          <Field label="Reopens"       value={task.reopenCount || 0} />
          <Field label="Speed factor"  value={(task.metrics?.speedFactor || 0).toFixed(2)} />
          <Field label="Delay class"   value={task.metrics?.delayClass || "—"} />
        </div>
      </Card>

      <Card padding="none">
        <div style={{ padding: "14px 16px", borderBottom: "1px solid var(--border-subtle)", fontSize: 14, fontWeight: 600, color: "var(--text-primary)" }}>
          Lifecycle events
        </div>
        {events.length === 0 ? (
          <EmptyState icon="⌛" title="No events recorded for this task" />
        ) : (
          <ol style={{ listStyle: "none", padding: 0, margin: 0 }}>
            {events.map((e, i) => (
              <li key={e._id} style={{ padding: "12px 16px", borderTop: i === 0 ? "none" : "1px solid var(--border-subtle)", display: "flex", alignItems: "flex-start", gap: 12 }}>
                <div style={{ fontSize: 18, lineHeight: 1, marginTop: 2 }}>{TYPE_ICON[e.type] || "•"}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <Pill tone={TYPE_TONE[e.type] || "neutral"} size="sm">{e.type}</Pill>
                  </div>
                  <div style={{ fontSize: 11, color: "var(--text-tertiary)", marginTop: 4 }}>
                    {fmt(e.createdAt)}
                    {e.actor && (
                      <>
                        {" · "}
                        <RouterLink to={`/users/${e.actor._id}`} style={{ color: "var(--accent-text)", textDecoration: "none", fontWeight: 500 }}>
                          {e.actor.username}
                        </RouterLink>
                      </>
                    )}
                  </div>
                  {e.meta && Object.keys(e.meta).length > 0 && (
                    <div style={{ fontSize: 11, color: "var(--text-tertiary)", marginTop: 6, fontFamily: "JetBrains Mono, ui-monospace, monospace", wordBreak: "break-all" }}>
                      {Object.entries(e.meta).map(([k, v]) => (
                        <span key={k} style={{ marginRight: 12 }}>
                          <span style={{ opacity: 0.55 }}>{k}=</span>
                          {typeof v === "object" ? JSON.stringify(v) : String(v)}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ol>
        )}
      </Card>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: 10, color: "var(--text-tertiary)", textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 600 }}>{label}</div>
      <div className="tabular" style={{ fontSize: 14, fontWeight: 600, color: "var(--text-primary)", marginTop: 4 }}>{value}</div>
    </div>
  );
}
